import { useEffect, useState } from "react";
import { PredictiveArcCanvas } from "@designcodeio/threeui";
import "@designcodeio/threeui/style.css";

const HERO_UI_SCREENS = [
  ["empty-selection", "Empty selection"],
  ["gradient-analysis", "Gradient analysis"],
  ["selection-ready", "Selection ready"],
  ["color-tokens", "Color tokens"],
  ["scan-progress", "Scan progress"],
  ["smart-fix", "Gradient-safe Smart Fix"],
  ["settings-workspace", "Workspace settings"],
  ["color-blindness", "Color blindness simulation"],
  ["scan-results", "Scan results"],
  ["layer-specs", "Layer specifications"],
  ["settings-analysis", "Analysis settings"],
  ["detail-results", "Result list"],
] as const;

function useSiteTheme(): "light" | "dark" {
  const read = () =>
    typeof document !== "undefined" && document.documentElement.dataset.theme === "dark"
      ? "dark"
      : "light";
  const [theme, setTheme] = useState<"light" | "dark">(read);
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setTheme(root.dataset.theme === "dark" ? "dark" : "light");
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    window.addEventListener("tintsense-theme", sync);
    sync();
    return () => {
      observer.disconnect();
      window.removeEventListener("tintsense-theme", sync);
    };
  }, []);
  return theme;
}

export function Scene() {
  const theme = useSiteTheme();
  return (
    <div className="shader-frame">
      <PredictiveArcCanvas
        mode={theme}
        color="gray"
        speed={1.00}
        hue={0}
        saturation={0}
        brightness={1.00}
        background={theme === "dark" ? "#121310" : "#ffffff"}
      />
      <div className="hero-state-arc" aria-label="TintSense interface states">
        <svg viewBox="0 0 1200 420" role="img" aria-labelledby="hero-state-arc-title">
          <title id="hero-state-arc-title">TintSense plugin screens moving along a predictive arc</title>
          <path className="hero-state-arc__line" d="M 0 250 Q 600 20 1200 250" />
          {HERO_UI_SCREENS.map(([slug, label], index) => (
            <g className="hero-state-arc__motion" key={slug}>
              <image className="hero-state-arc__screen--light" x="-96.5" y="-142" width="193" height="284" href={`/ui-states/light/${slug}.png`} aria-label={`${label} screen, light theme`} />
              <image className="hero-state-arc__screen--dark" x="-96.5" y="-142" width="193" height="284" href={`/ui-states/dark/${slug}.png`} aria-label={`${label} screen, dark theme`} />
              <rect className="hero-state-arc__frame" x="-96.5" y="-142" width="193" height="284" rx="6.5" ry="6.5" />
              <animateMotion dur="48s" begin={`-${index * 4}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1" calcMode="linear" path="M 1800 605 Q 600 -315 -600 605" />
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
